<template>
  <div id="builder">
    <div class="welcome-message">
      <section v-if="!isBuildComplete" class="home-progress" aria-labelledby="builder-welcome-title">
        <div class="home-progress__head">
          <div>
            <span class="welcome-kicker">{{ selectedCount ? 'Build in progress' : 'PC Builder' }}</span>
            <h2 id="builder-welcome-title">{{ selectedCount ? nextStepTitle : 'Build your PC' }}</h2>
            <p>{{ selectedCount ? nextStepDescription : 'Start with a processor. We’ll help you find matching parts.' }}</p>
          </div>
          <span class="home-progress__count">{{ selectedCount }} / {{ totalPartCount }} parts</span>
        </div>
        <div v-if="selectedCount" class="build-progress" :aria-label="selectedCount + ' of ' + totalPartCount + ' parts selected'">
          <span v-for="part in buildParts" :key="part.key" class="build-progress__segment" :class="{ 'is-selected': isPartSelected(part) }"></span>
        </div>
        <button type="button" class="btn btn-primary home-progress__continue" @click="continueBuild">{{ nextStepAction }} <span aria-hidden="true">&rarr;</span></button>
        <details v-if="selectedCount" class="home-analysis">
          <summary>Build analysis <span v-if="compatibilitySummary.conflicts || compatibilitySummary.warnings" class="home-analysis__warning">{{ compatibilitySummary.conflicts + compatibilitySummary.warnings }} to review</span></summary>
          <div class="build-health" aria-label="Live build analysis">
            <article v-for="card in healthCards" :key="card.key" class="health-card" :class="'health-card--' + card.tone">
              <div class="health-card__topline"><span>{{ card.label }}</span></div>
              <strong>{{ card.value }}</strong>
              <p>{{ card.detail }}</p>
            </article>
          </div>
        </details>
      </section>

      <!-- Completion state: every slot is filled, so the checklist gives way to
           the parts list and the one-click Amazon handoff. -->
      <BuyBuildPanel
        v-if="isBuildComplete"
        :rows="buildRows"
        :compatibility="compatibilityPayload"
        :power="powerPayload"
        :balance="balancePayload"
        :performance="performancePercent"
        :total="buildTotal"
        :show-parts="false"
        compact
        source="builder_completion"
        report-completion
      >
        <template #secondary>
          <span v-if="saveCtaDisabled" class="home-saved"><span class="icon icon-check" aria-hidden="true"></span> Saved to your account</span>
          <button
            v-else
            type="button"
            class="builder-save-build"
            @click="continueBuild"
          >
            <span class="icon icon-save" aria-hidden="true"></span>
            <span>{{ saveCtaLabel }}</span>
          </button>
        </template>
      </BuyBuildPanel>

      <!-- Your saved builds, with the one currently open marked. Signed-out
           visitors have nothing to list, so the whole block stays hidden. -->
      <details v-if="showSavedBuilds" class="home-builds" aria-labelledby="home-builds-title">
        <summary class="home-builds__summary">
          <span id="home-builds-title">Saved builds</span>
          <span>{{ savedBuildOverview }}</span>
        </summary>
        <div class="home-builds__head">
          <span>Pick up another build</span>
          <router-link :to="{ path: '/users/mybuild' }">View all builds &rarr;</router-link>
        </div>

        <ul v-if="savedBuildSummaries.length" class="home-builds__list">
          <li
            v-for="build in savedBuildSummaries"
            :key="build.id"
            class="home-builds__item"
            :class="{ 'is-current': build.isCurrent }"
          >
            <BuildSummaryCard
              variant="minimal"
              :build="build"
              :total-parts="totalPartCount"
              :status="buildStatusLabel(build)"
              :current="build.isCurrent"
              :dirty="build.isCurrent && hasUnsavedChanges"
              :disabled="build.isCurrent"
              :show-prices="showPrices"
              :action-label="build.isCurrent ? build.name + ' is currently open' : 'Open ' + build.name"
              @activate="openSavedBuild"
            />
          </li>
        </ul>

        <p v-else-if="ifBuildsLoaded" class="home-builds__empty">
          No saved builds yet. Save your current parts list to find it here later.
        </p>
      </details>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { SHOW_PRICES } from '../../config'
import { BUILD_PARTS, getPartDefinition } from '../../partOrder'
import { functions } from '../../functions'
import { partName, rowsFromMenuOptions, rowsFromSavedBuild, summarizeRows } from '../../utils/buildParts'
import { formatCompatMessage } from '../../utils/compatMessage'
import BuyBuildPanel from './BuyBuildPanel.vue'
import BuildSummaryCard from './BuildSummaryCard.vue'
import analytics from '../../services/analytics'
import { EventBus } from '../../eventBus'

const STEP_COPY = {
  cpu: 'The processor sets the socket and memory rules for the rest of your build.',
  graphics: 'Choose the graphics card that matches your games, work, and display goals.',
  motherboard: 'Pick a board that supports your processor and the connections you need.',
  ram: 'Add memory that matches your board and the way you plan to use the PC.',
  storage: 'Choose enough fast storage for your system, applications, and files.',
  case: 'Find a case with room for your parts and the airflow your build needs.',
  psu: 'Finish with a power supply sized for the hardware you selected.',
}

const PSU_WATTAGE_STEPS = [450, 500, 550, 600, 650, 700, 750, 800, 850, 900, 1000, 1200, 1300, 1500, 1600]

export default {
  name: 'BuildHome',
  components: {
    BuyBuildPanel,
    BuildSummaryCard,
  },
  data(){
    return {
      lastFetchedUserId: null,
    }
  },
  watch: {
    getUser: {
      immediate: true,
      handler(){ this.loadSavedBuilds() },
    },
  },
  computed: {
    ...mapGetters([
      'allMenuOptions',
      'getBuildTDP',
      'getTotalCost',
      'getBuidStatus',
      'getWarnings',
      'getStateCompatTable',
      'getUser',
      'getUserBuilds',
      'ifBuildsLoaded',
      'loggedIn',
      'getSavedBuildId',
      'hasUnsavedChanges',
    ]),
    showSavedBuilds(){
      return this.loggedIn == 'true'
    },
    saveCtaDisabled(){
      return this.loggedIn == 'true' && this.getSavedBuildId !== null && !this.hasUnsavedChanges
    },
    saveCtaLabel(){
      if(this.saveCtaDisabled){ return 'Saved to your account' }
      if(this.loggedIn != 'true'){ return 'Save this build to your account' }
      return this.getSavedBuildId !== null ? 'Save changes' : 'Save build'
    },
    // Newest first, capped: this is an at-a-glance list, not the library.
    savedBuildSummaries(){
      var builds = Array.isArray(this.getUserBuilds) ? this.getUserBuilds : []
      return builds
        .map((build) => {
          var rows = rowsFromSavedBuild(build)
          var summary = summarizeRows(rows)
          var partCount = BUILD_PARTS.filter((part) => {
            var value = build[part.backendKey]
            return Array.isArray(value) ? value.length > 0 : !!value
          }).length
          return {
            id: build.id,
            raw: build,
            name: build.name || 'Untitled build',
            image: this.savedBuildImage(build),
            cpu: this.savedBuildPartName(build.processor),
            gpu: this.savedBuildPartName(build.graphics),
            partCount: partCount,
            complete: partCount === this.totalPartCount,
            progress: this.totalPartCount ? Math.round(partCount / this.totalPartCount * 100) : 0,
            hasPrice: summary.pricedItemCount > 0,
            totalPrice: this.formatMoney(summary.totalCost),
            tdp: summary.tdp,
            // String compare: ids arrive as numbers from the API and can be
            // persisted as strings in the builder's local state.
            isCurrent: this.getSavedBuildId !== null
              && String(build.id) === String(this.getSavedBuildId),
          }
        })
        .sort((left, right) => {
          if(left.isCurrent !== right.isCurrent){ return left.isCurrent ? -1 : 1 }
          return Number(right.id || 0) - Number(left.id || 0)
        })
        .slice(0, 3)
    },
    savedBuildOverview(){
      var builds = Array.isArray(this.getUserBuilds) ? this.getUserBuilds : []
      var completeCount = builds.filter((build) => BUILD_PARTS.every((part) => {
        var value = build[part.backendKey]
        return Array.isArray(value) ? value.length > 0 : !!value
      })).length
      var label = builds.length + ' saved configuration' + (builds.length === 1 ? '' : 's')
      if(completeCount){
        label += ' \u00b7 ' + completeCount + ' complete'
      }
      return label
    },
    showPrices(){
      return SHOW_PRICES
    },
    buildParts(){
      return BUILD_PARTS
    },
    totalPartCount(){
      return this.buildParts.length
    },
    selectedCount(){
      return this.buildParts.filter((part) => this.isPartSelected(part)).length
    },
    nextPart(){
      return this.buildParts.find((part) => !this.isPartSelected(part)) || null
    },
    isBuildComplete(){
      return this.selectedCount === this.totalPartCount
    },
    estimatedPower(){
      var watts = parseInt(this.getBuildTDP)
      return isNaN(watts) ? 0 : Math.max(0, watts)
    },
    selectedPsu(){
      return this.primarySelection('psu')
    },
    selectedPsuWattage(){
      var psu = this.selectedPsu
      if(!psu){ return null }
      var raw = (psu.specs && psu.specs.wattage) || (psu.compat && psu.compat.wattage)
      var watts = parseInt(raw)
      return isNaN(watts) || watts <= 0 ? null : watts
    },
    recommendedPsuWattage(){
      if(!this.estimatedPower){ return null }
      var required = Math.max(450, Math.ceil(this.estimatedPower / 0.8))
      for(var i = 0; i < PSU_WATTAGE_STEPS.length; i++){
        if(PSU_WATTAGE_STEPS[i] >= required){ return PSU_WATTAGE_STEPS[i] }
      }
      return Math.ceil(required / 100) * 100
    },
    compatibilitySummary(){
      var table = this.getStateCompatTable || {}
      var seen = {}
      var result = { checked: 0, conflicts: 0, warnings: 0, firstIssue: '', conflictMessages: [], warningMessages: [] }

      Object.keys(table).forEach((type) => {
        var withTypes = table[type] || {}
        Object.keys(withTypes).forEach((withType) => {
          var checks = withTypes[withType] || {}
          Object.keys(checks).forEach((option) => {
            var entry = checks[option]
            if(!entry){ return }
            var message = this.cleanStatusMessage(entry.message)
            var key = message || [type, withType, option, entry.compatible, !!entry.warning].join(':')
            if(seen[key]){ return }
            seen[key] = true
            result.checked++
            var finding = { text: message, types: [type, withType] }
            if(entry.compatible === false){
              result.conflicts++
              if(message){ result.conflictMessages.push(finding) }
              if(!result.firstIssue){ result.firstIssue = message }
            }
            else if(entry.warning){
              result.warnings++
              if(message){ result.warningMessages.push(finding) }
              if(!result.firstIssue){ result.firstIssue = message }
            }
          })
        })
      })

      if(!result.warnings && this.getWarnings && this.getWarnings.length){
        result.warnings = this.getWarnings.length
        result.warningMessages = this.getWarnings
          .map((warning) => ({ text: this.cleanStatusMessage(warning), types: [] }))
          .filter((finding) => finding.text)
        result.firstIssue = result.firstIssue || (result.warningMessages[0] && result.warningMessages[0].text) || ''
      }
      return result
    },
    // Rows, totals, and health readings the completed-build panel renders. The
    // live build lives in the menu options, so it is normalized the same way a
    // saved build is.
    buildRows(){
      return rowsFromMenuOptions(this.allMenuOptions)
    },
    buildTotal(){
      var total = functions.parsePrice(this.getTotalCost)
      return total === null ? 0 : total
    },
    performancePercent(){
      var result = functions.getBuildPerformance(this.allMenuOptions) || {}
      return Math.min(100, Math.round((result.Total || 0) / 160 * 100))
    },
    compatibilityPayload(){
      var summary = this.compatibilitySummary
      // Conflicts first: they are the reason someone should hesitate before buying.
      var issues = summary.conflictMessages.concat(summary.warningMessages)
      return {
        conflicts: summary.conflicts || (this.getBuidStatus == 'sad' ? 1 : 0),
        warnings: summary.warnings || (this.getBuidStatus == 'warning' ? 1 : 0),
        issues: issues.slice(0, 5).map((finding) => ({
          text: finding.text,
          parts: this.compatIssueParts(finding.types),
        })),
      }
    },
    powerPayload(){
      return {
        watts: this.estimatedPower,
        wattage: this.selectedPsuWattage,
      }
    },
    // The one status card the buy panel had no equivalent for, so it moves across
    // rather than being lost when the status panel collapses.
    balancePayload(){
      var card = this.balanceCard
      return { value: card.value, detail: card.detail, tone: card.tone }
    },
    compatibilityCard(){
      var summary = this.compatibilitySummary
      if(!this.selectedCount){
        return this.healthCard('compatibility', 'Compatibility', 'Waiting for parts', 'Checks begin after you select compatible component categories.', 'idle', '○')
      }
      if(summary.conflicts || this.getBuidStatus == 'sad'){
        var conflictCount = summary.conflicts || 1
        return this.healthCard('compatibility', 'Compatibility', conflictCount + (conflictCount === 1 ? ' conflict' : ' conflicts'), summary.firstIssue || 'Review the highlighted parts before continuing.', 'danger', '!')
      }
      if(summary.warnings || this.getBuidStatus == 'warning'){
        var warningCount = summary.warnings || 1
        return this.healthCard('compatibility', 'Compatibility', 'Compatible — ' + warningCount + (warningCount === 1 ? ' warning' : ' warnings') + ' to review', summary.firstIssue || 'A compatibility warning is available to review.', 'warning', '!')
      }
      return this.healthCard('compatibility', 'Compatibility', 'No conflicts', summary.checked ? summary.checked + ' checks passed across the selected parts.' : 'Selected parts fit together so far.', 'good', '✓')
    },
    powerCard(){
      if(!this.estimatedPower){
        return this.healthCard('power', 'Power', 'Waiting for parts', 'Estimated draw appears as components are selected.', 'idle', 'ϟ', 0)
      }
      if(!this.selectedPsuWattage){
        var suggested = this.recommendedPsuWattage
        var suggestedProgress = suggested ? Math.min(100, Math.round(this.estimatedPower / suggested * 100)) : 0
        return this.healthCard('power', 'Power', this.estimatedPower + ' W estimated', 'No PSU selected · ' + suggested + ' W or higher suggested.', 'info', 'ϟ', suggestedProgress)
      }

      var ratio = this.estimatedPower / this.selectedPsuWattage
      var progress = Math.min(100, Math.round(ratio * 100))
      if(ratio > 1){
        return this.healthCard('power', 'Power', 'PSU overloaded', this.estimatedPower + ' W draw exceeds the selected ' + this.selectedPsuWattage + ' W supply.', 'danger', '!', progress)
      }
      if(ratio > 0.8){
        return this.healthCard('power', 'Power', 'Headroom is tight', this.estimatedPower + ' W of ' + this.selectedPsuWattage + ' W · consider ' + this.recommendedPsuWattage + ' W or higher.', 'warning', 'ϟ', progress)
      }
      return this.healthCard('power', 'Power', (this.selectedPsuWattage - this.estimatedPower) + ' W headroom', this.estimatedPower + ' W estimated draw on a ' + this.selectedPsuWattage + ' W supply.', 'good', 'ϟ', progress)
    },
    balanceCard(){
      var cpu = this.primarySelection('cpu')
      var gpu = this.primarySelection('graphics')
      if(!cpu && !gpu){
        return this.healthCard('balance', 'CPU / GPU balance', 'Not enough data', 'Select a processor and graphics card to compare their performance tiers.', 'idle', '↔')
      }
      if(!cpu || !gpu){
        return this.healthCard('balance', 'CPU / GPU balance', 'One part missing', 'Add ' + (cpu ? 'a graphics card' : 'a processor') + ' to estimate system balance.', 'info', '↔')
      }

      var cpuScore = this.performanceScore(cpu)
      var gpuScore = this.performanceScore(gpu)
      if(cpuScore === null || gpuScore === null){
        return this.healthCard('balance', 'CPU / GPU balance', 'Analysis pending', 'Performance data is not available for both selected parts yet.', 'idle', '↔')
      }

      var difference = Math.abs(cpuScore - gpuScore)
      if(difference <= 15){
        return this.healthCard('balance', 'CPU / GPU balance', 'Well matched', 'The selected CPU and GPU sit in similar performance tiers.', 'good', '↔')
      }
      if(cpuScore > gpuScore){
        return this.healthCard('balance', 'CPU / GPU balance', 'CPU-focused', 'Processor performance is ' + difference + ' points ahead; this build favors CPU-heavy workloads.', difference > 30 ? 'warning' : 'info', '↔')
      }
      return this.healthCard('balance', 'CPU / GPU balance', 'GPU-focused', 'Graphics performance is ' + difference + ' points ahead; this build favors graphics-heavy workloads.', difference > 30 ? 'warning' : 'info', '↔')
    },
    costOrProgressCard(){
      var remaining = this.totalPartCount - this.selectedCount
      if(this.showPrices){
        var total = parseFloat(this.getTotalCost)
        var value = isNaN(total) ? '$0.00' : '$' + this.formatMoney(total)
        return this.healthCard('price', 'Estimated total', value, this.selectedCount + ' of ' + this.totalPartCount + ' component categories selected.', 'info', '$')
      }
      return this.healthCard('progress', 'Build progress', this.selectedCount + '/' + this.totalPartCount + ' selected', remaining ? remaining + (remaining === 1 ? ' component category remains.' : ' component categories remain.') : 'Every core component category is filled.', this.isBuildComplete ? 'good' : 'info', '#')
    },
    healthCards(){
      return [this.compatibilityCard, this.powerCard, this.balanceCard, this.costOrProgressCard]
    },
    // The next-step aside only renders while the build is incomplete, so these
    // can rely on nextPart being present.
    nextStepNumber(){
      if(!this.nextPart){ return this.totalPartCount }
      return this.buildParts.findIndex((part) => part.key === this.nextPart.key) + 1
    },
    nextStepTitle(){
      return 'Choose ' + this.nextPart.label
    },
    nextStepDescription(){
      if(this.nextPart.key === 'psu' && this.estimatedPower && this.recommendedPsuWattage){
        return 'Your selected parts estimate ' + this.estimatedPower + ' W. Aim for at least ' + this.recommendedPsuWattage + ' W for operating headroom.'
      }
      return STEP_COPY[this.nextPart.key]
    },
    nextStepAction(){
      return 'Choose ' + this.nextPart.label
    },
  },
  methods: {
    ...mapActions([
      'getLastRoute',
      'fetchUserBuilds',
      'loadBuild',
    ]),
    resolveUserId(){
      var user = this.getUser
      if(typeof user == 'string'){
        try { user = JSON.parse(user) } catch(e) { user = null }
      }
      return user && user.id ? user.id : null
    },
    loadSavedBuilds(){
      var userId = this.resolveUserId()
      if(!userId || this.lastFetchedUserId == userId){ return }
      this.lastFetchedUserId = userId
      this.fetchUserBuilds(userId)
    },
    openSavedBuild(build){
      if(build.isCurrent){ return }
      this.loadBuild(build.raw)
    },
    buildStatusLabel(build){
      if(build.isCurrent && this.hasUnsavedChanges){ return 'Unsaved changes' }
      if(build.isCurrent){ return 'Currently open' }
      if(build.complete){ return 'Complete' }
      var remaining = this.totalPartCount - build.partCount
      return remaining + (remaining === 1 ? ' part left' : ' parts left')
    },
    savedBuildPartName(value){
      var item = Array.isArray(value) ? value[0] : value
      return item ? partName(item) : ''
    },
    savedBuildImage(build){
      var preferred = [build.case, build.processor, build.graphics, build.motherboard]
      for(var i = 0; i < preferred.length; i++){
        var item = Array.isArray(preferred[i]) ? preferred[i][0] : preferred[i]
        if(!item){ continue }
        if(item.mainImg){ return item.mainImg }
        if(!item.imgUrl){ continue }
        try {
          var images = JSON.parse(item.imgUrl)
          if(Array.isArray(images) && images.length){
            if(typeof images[0] == 'string' && images[0].includes('<a href')){ return images[1] || '' }
            return images[0]
          }
        } catch(e){ /* Continue to the next useful part image. */ }
      }
      return build.image && typeof build.image == 'string' ? build.image : ''
    },
    updateRoute(){
      this.getLastRoute(this.$route.path)
    },
    isPartSelected(part){
      var option = this.allMenuOptions && this.allMenuOptions[part.menuKey]
      var contents = option && option.item ? option.item.contents : null
      if(Array.isArray(contents)){
        return contents.length > 0
      }
      return contents !== null && contents !== undefined
    },
    selections(menuKey){
      var option = this.allMenuOptions && this.allMenuOptions[menuKey]
      var contents = option && option.item ? option.item.contents : null
      if(Array.isArray(contents)){ return contents.filter(Boolean) }
      return contents ? [contents] : []
    },
    // A finding is recorded against two part categories. Resolve each to the
    // part actually sitting in that slot, with a link that opens it in its
    // category slider — the same destination a checklist row leads to.
    compatIssueParts(types){
      var seen = {}
      var parts = []
      ;(types || []).forEach((type) => {
        var definition = getPartDefinition(type)
        if(!definition || seen[definition.menuKey]){ return }
        seen[definition.menuKey] = true
        var item = this.primarySelection(definition.menuKey)
        if(!item){ return }
        parts.push({
          key: definition.menuKey,
          category: definition.label,
          label: partName(item),
          to: { path: '/builder/' + definition.url, query: { view: 'slider', id: item.id } },
        })
      })
      // One named part explains nothing about a conflict between two.
      return parts.length > 1 ? parts : []
    },
    primarySelection(menuKey){
      var selected = this.selections(menuKey)
      return selected.length ? selected[0] : null
    },
    performanceScore(item){
      var score = Number(item && item.performance)
      return isNaN(score) || !isFinite(score) ? null : Math.max(0, Math.min(100, Math.round(score)))
    },
    cleanStatusMessage(message){
      return formatCompatMessage(message)
    },
    formatMoney(value){
      return Number(value || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    healthCard(key, label, value, detail, tone, symbol, progress){
      return {
        key: key,
        label: label,
        value: value,
        detail: detail,
        tone: tone,
        symbol: symbol,
        progress: progress === undefined ? null : progress,
      }
    },
    continueBuild(){
      var destination = this.isBuildComplete ? 'save' : this.nextPart.url
      analytics.trackClick('builder-next-step', {
        destination: destination,
        selectedCount: this.selectedCount,
        totalPartCount: this.totalPartCount,
        compatibility: this.compatibilityCard.value,
        estimatedPower: this.estimatedPower,
        recommendedPsuWattage: this.recommendedPsuWattage,
      })
      if(this.isBuildComplete){
        EventBus.$emit('request-build-save')
        return
      }
      this.$router.push(destination).catch(()=>{})
    },
  },
}
</script>

<style scoped>
.welcome-message { width: 100%; min-width: 0; padding: 0.55rem 1rem 1.35rem; color: #cfd2da; }
.welcome-message summary:focus-visible, .welcome-message button:focus-visible { outline: 2px solid #7fd0ee; outline-offset: 4px; }
.welcome-kicker { display: block; color: #1997c6; font-size: 11px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; }
.build-progress { display: flex; gap: 5px; height: 4px; }
.build-progress__segment { flex: 1; border-radius: 3px; background: #293241; }
.build-progress__segment.is-selected { background: #1997c6; }
.build-health { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1px; margin-top: 14px; border: 1px solid rgba(67,72,87,0.5); border-radius: 8px; background: rgba(67,72,87,0.3); overflow: hidden; }
.health-card { min-width: 0; padding: 16px; background: #0d141e; }
.health-card__topline { margin-bottom: 7px; color: #8f98aa; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.health-card strong { display: block; color: #e7ebf3; font-size: 15px; line-height: 1.4; }
.health-card.health-card--warning strong { color: #e0c07e; }
.health-card.health-card--danger strong { color: #f06b79; }
.health-card.health-card--good strong { color: #8fe8c4; }
.home-progress .health-card p { margin: 6px 0 0; color: #9198aa; font-size: 12px; line-height: 1.5; }
.builder-save-build { display: inline-flex; align-items: center; gap: 7px; min-height: 36px; padding: 8px 12px; border: 1px solid rgba(25,151,198,0.5); border-radius: 6px; background: rgba(25,151,198,0.08); color: #9bdff5; font-size: 12px; cursor: pointer; }
.builder-save-build:hover { border-color: #1997c6; background: rgba(25,151,198,0.16); }
.home-builds { margin-top: 14px; padding: 18px 20px; border: 1px solid rgba(67,72,87,0.6); border-radius: 10px; background: rgba(8,11,17,0.6); }
.home-builds__head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.home-builds__head a { color: #7fd0ee; font-size: 12px; text-decoration: none; }
.home-builds__head a:hover { text-decoration: underline; }
.home-builds__list { display: grid; gap: 8px; margin: 0; padding: 0; list-style: none; }
.home-builds__item { min-width: 0; }
.home-builds__empty { margin: 0; color: #9198aa; font-size: 12px; line-height: 1.6; }
.home-progress { padding: 24px; border: 1px solid rgba(25,151,198,0.4); border-radius: 10px; background: linear-gradient(160deg, rgba(8,27,37,0.6), rgba(6,11,18,0.78)); }
.home-progress__head { display: flex; justify-content: space-between; gap: 20px; align-items: flex-start; }
.home-progress h2 { margin: 8px 0; color: #e7ebf3; font-size: 24px; font-weight: 600; }
.home-progress p { max-width: 580px; margin: 0 0 20px; color: #a4acbc; font-size: 13px; line-height: 1.6; }
.home-progress__count { flex-shrink: 0; color: #9198aa; font-size: 12px; }
.home-progress .build-progress { max-width: 400px; margin-bottom: 20px; }
.home-progress__continue { display: inline-flex; align-items: center; gap: 24px; padding: 10px 16px; font-size: 13px; }
.home-analysis { margin-top: 24px; padding-top: 14px; border-top: 1px solid rgba(67,72,87,0.5); }
.home-analysis summary { color: #a4acbc; font-size: 12px; cursor: pointer; }
.home-analysis__warning { margin-left: 10px; color: #e0c07e; }
.home-saved { display: inline-flex; align-items: center; gap: 7px; color: #8fe8c4; font-size: 12px; }
.home-builds__summary { color: #cfd2da; font-size: 14px; cursor: pointer; }
.home-builds__summary > span:first-child { font-weight: 600; }
.home-builds__summary > span + span { margin-left: 12px; color: #8f98aa; font-size: 12px; }
.home-builds[open] .home-builds__head { margin-top: 20px; padding-top: 14px; border-top: 1px solid rgba(67,72,87,0.5); color: #9198aa; font-size: 12px; }
.home-builds .home-builds__list { grid-template-columns: 1fr; }
@media (max-width: 600px) {
  .home-progress { padding: 18px; }
  .home-progress__head { flex-direction: column-reverse; gap: 12px; }
  .home-builds__summary > span + span { display: block; margin: 5px 0 0 18px; }
}
@media (max-width: 600px) {
  .welcome-message { padding: 0.5rem 0.5rem 1rem; }
  .build-health { grid-template-columns: 1fr; }
}
</style>
